import { PaytrieAPI } from '@paytrie/client'

import {
  CORPORATE_LOGIN_PATH,
  CORPORATE_UPLOAD_PATH,
  ROUTES,
} from '@/constants/corporateRoutePaths'
import {
  FormStates,
  type StateValue,
} from '@/panels/corporatesignup/FormStateMachine'
import { STATE_TO_ROUTE, WIZARD_STATE_ORDER } from '@/utils/corporateWizard'

export type CorporateEnrollment = PaytrieAPI.Corporation

const filled = (value: unknown) =>
  value !== null && value !== undefined && value !== ''

type Gate =
  | ((enrollment: CorporateEnrollment) => boolean)
  | 'shared-with-previous'

const GATES: Record<StateValue, Gate> = {
  [FormStates.CORPINFO]: (e) =>
    filled(e.corporationName) &&
    filled(e.corporationNumber) &&
    filled(e.corporationEmail) &&
    filled(e.phone),
  [FormStates.CORP_DATE_DETAILS]: (e) =>
    filled(e.dateOfIncorporation) && filled(e.tpd),
  [FormStates.CORP_BUS_ADDRESS]: (e) => filled(e.address),
  [FormStates.CORP_ADDN_DETAILS]: (e) =>
    filled(e.businessNature) &&
    filled(e.purpose) &&
    filled(e.sourceOfFunds) &&
    filled(e.msbRegistered),
  [FormStates.DIRECTOR_INFO]: (e) => e.directors.length > 0,
  [FormStates.DIRECTOR_PERSONAL_SHARE]: (e) =>
    filled(e.hasBeneficialOwners) &&
    (e.hasBeneficialOwners === false || e.beneficialOwners.length > 0),
  [FormStates.DIRECTOR_PEP_INFO]: (e) => filled(e.isPep),
  [FormStates.DIRECTOR_AUTH_INFO]: (e) => filled(e.authorizedUser),
  [FormStates.CORPDOCS]: (e) => e.documentsSubmitted,
  [FormStates.CORPORATE_ARTICLE_INCORPORATION]: 'shared-with-previous',
  [FormStates.CORPORATE_ASSIST]: 'shared-with-previous',
  [FormStates.COMPLETE]: 'shared-with-previous',
}

type Step = {
  paths: string[]
  isComplete: (enrollment: CorporateEnrollment) => boolean
}

const STEPS: Step[] = WIZARD_STATE_ORDER.reduce<Step[]>((steps, state) => {
  const gate = GATES[state]
  const path = STATE_TO_ROUTE[state]

  if (gate === 'shared-with-previous') {
    const previous = steps[steps.length - 1]
    if (previous) previous.paths.push(path)
    return steps
  }

  return [...steps, { paths: [path], isComplete: gate }]
}, [])

export const CORPORATE_START_PATH = ROUTES.CORPORATE_INFO
export const CORPORATE_REVIEW_PATH = ROUTES.COMPLETE

const stepIndexOf = (path: string) =>
  STEPS.findIndex((step) => step.paths.includes(path))

const firstIncompleteIndex = (enrollment: CorporateEnrollment) =>
  STEPS.findIndex((step) => !step.isComplete(enrollment))

export const getCorporateResumeStep = (
  enrollment: CorporateEnrollment | null,
  isAuthenticated: boolean
): string | null => {
  if (!isAuthenticated) return CORPORATE_LOGIN_PATH
  if (!enrollment) return CORPORATE_START_PATH

  if (enrollment.status !== 'draft') return CORPORATE_REVIEW_PATH

  const next = firstIncompleteIndex(enrollment)
  return next === -1 ? CORPORATE_REVIEW_PATH : STEPS[next].paths[0]
}

export const isStepReachable = (
  path: string,
  enrollment: CorporateEnrollment | null
): boolean => {
  if (!enrollment) return false
  if (path === CORPORATE_UPLOAD_PATH) return enrollment.status !== 'draft'
  if (enrollment.status !== 'draft') return false

  const target = stepIndexOf(path)
  if (target === -1) return false

  const firstIncomplete = firstIncompleteIndex(enrollment)
  return firstIncomplete === -1 || target <= firstIncomplete
}
